/* Version « particuliers » de la page Fournisseurs partenaires.
   Copie autonome : elle peut diverger librement de la version pro. */
import { useEffect, useMemo, useState } from "react";
import { Check, Eye, HeartHandshake, Scale, Search, BarChart3, ArrowRight, Users, Building2, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import HowSteps from "@/components/v2/HowSteps";
import {
  SecondaryCTA,
  Section,
  Steps,
  ProcessTimeline,
  Bullets,
  RelatedLinks,
  FinalCTA,
} from "@/components/v2/blocks";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/lib/api";

const PAGE_TITLE = "Fournisseurs d'énergie suivis — particuliers | Energy Market";
const PAGE_DESC =
  "Energy Market suit les principaux fournisseurs d'électricité et de gaz en France. Approche indépendante, étude gratuite et sans engagement pour particuliers et professionnels.";

const LOGO_MAP = {
  edf: "edf",
  engie: "engie",
  totalenergies: "totalenergies",
  "total energies": "totalenergies",
  eni: "eni",
  ekwateur: "ekwateur",
  "octopus energy": "octopus",
  octopus: "octopus",
  ilek: "ilek",
  "ohm énergie": "ohm-energie",
  "ohm energie": "ohm-energie",
  alterna: "alterna",
  "mint énergie": "mint",
  "mint energie": "mint",
  mint: "mint",
};

function logoFor(name) {
  const k = (name || "").trim().toLowerCase();
  const key = LOGO_MAP[k];
  return key ? `/img/suppliers/${key}.png` : null;
}

const FALLBACK = [
  { name: "EDF" }, { name: "ENGIE" }, { name: "TotalEnergies" }, { name: "Eni" },
  { name: "Ekwateur" }, { name: "Octopus Energy" }, { name: "Ilek" }, { name: "OHM Énergie" },
];

const HERO_REASSURANCE = ["Étude gratuite", "Sans engagement", "Particuliers & professionnels"];
const HERO_BG = "/img/hero/fournisseurs-partenaires-part.jpg";

const PROCESS = [
  { title: "Analyse de votre situation", text: "Nous étudions vos contrats, consommations et besoins spécifiques." },
  { title: "Consultation du marché", text: "Nous identifions les fournisseurs pertinents pour votre profil." },
  { title: "Présentation comparative", text: "Nous vous expliquons les offres disponibles de manière claire." },
  { title: "Accompagnement dans la décision", text: "Nous vous guidons jusqu'au choix final, sans pression." },
];

const FAQ = [
  ["Energy Market est-il indépendant ?", "Oui. Energy Market n'est lié à aucun fournisseur. Chaque dossier est étudié individuellement, dans votre intérêt, sans parti pris."],
  ["La présence d'un fournisseur sur cette page signifie-t-elle une recommandation ?", "Non. Cette liste reflète les acteurs que nous suivons et consultons sur le marché. Elle ne constitue pas un classement ni une recommandation systématique."],
  ["Êtes-vous rémunéré par les fournisseurs ?", "Notre rémunération dépend du type de mission et est toujours expliquée en amont. Notre priorité reste de vous orienter vers la solution la plus adaptée à votre situation."],
  ["Puis-je rester chez mon fournisseur actuel ?", "Oui. L'étude peut simplement vous apporter une vision du marché, sans obligation de changement."],
  ["Travaillez-vous avec les particuliers et les professionnels ?", "Oui. Energy Market accompagne les deux profils partout en France, avec des approches adaptées à chaque situation."],
];

const ATOUTS = [
  { icon: Eye, title: "Une vision large du marché", text: "Nous suivons de nombreux fournisseurs pour comparer les solutions réellement adaptées." },
  { icon: Scale, title: "Une approche indépendante", text: "Chaque dossier est étudié individuellement, sans parti pris, dans votre intérêt." },
  { icon: HeartHandshake, title: "Un accompagnement humain", text: "Nous vous expliquons les offres et vous guidons jusqu'à la décision." },
];

function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
    return () => {
      document.title = "Energy Market — Votre courtier en énergie";
      if (meta) meta.setAttribute("content", "Energy Market — votre courtier en énergie pour les particuliers et les professionnels. Étude gratuite, sans engagement, accompagnement national.");
    };
  }, [title, description]);
}

function LogoTile({ s }) {
  const logo = s.logo || logoFor(s.name);
  const [err, setErr] = useState(!logo);

  return (
    <div className="mx-6 sm:mx-10 h-16 sm:h-20 w-32 shrink-0 flex items-center justify-center">
      {!err && logo ? (
        <img
          src={logo}
          alt={s.name}
          className="max-h-9 sm:max-h-10 max-w-[130px] object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition duration-300"
          onError={() => setErr(true)}
        />
      ) : (
        <span className="font-display text-sm sm:text-lg font-bold text-center transition-colors text-muted-foreground hover:text-primary">
          {s.name}
        </span>
      )}
    </div>
  );
}

function MarqueeRow({ items, dir }) {
  const doubled = [...items, ...items];
  return (
    <div className={`em-marq-row ${dir === "r" ? "em-marq-r" : "em-marq-l"}`}>
      {doubled.map((s, i) => (
        <LogoTile key={`${s.name}-${i}`} s={s} />
      ))}
    </div>
  );
}

export default function FournisseursPartenairesPart() {
  const [suppliers, setSuppliers] = useState(FALLBACK);

  usePageMeta(PAGE_TITLE, PAGE_DESC);

  useEffect(() => {
    api
      .get("/suppliers")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setSuppliers(r.data);
      })
      .catch(() => {});
  }, []);

  const rowR = useMemo(() => [...suppliers].reverse(), [suppliers]);

  return (
    <div>
      <style>{`
        .em-marq-mask { -webkit-mask-image: linear-gradient(to right, transparent, #000 4%, #000 96%, transparent); mask-image: linear-gradient(to right, transparent, #000 4%, #000 96%, transparent); }
        .em-marq-row { display: flex; width: max-content; }
        @keyframes em-marq-l { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes em-marq-r { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        .em-marq-l { animation: em-marq-l 40s linear infinite; }
        .em-marq-r { animation: em-marq-r 45s linear infinite; }
        .em-marq-mask:hover .em-marq-l, .em-marq-mask:hover .em-marq-r { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .em-marq-l, .em-marq-r { animation: none; } }
      `}</style>

      {/* HERO + LOGOS — plein écran */}
      <section className="relative isolate overflow-hidden -mt-20 min-h-[100svh] flex flex-col">
        <div className="relative flex-1 flex flex-col text-primary-foreground min-h-0">
          <div className="absolute inset-0 -z-10 bg-primary">
            {/* Photo de fond — parallax fixe */}
            <div
              className="absolute inset-0 bg-cover"
              style={{ backgroundImage: `url(${HERO_BG})`, backgroundAttachment: "fixed", backgroundPosition: "72% 35%" }}
            />
            {/* Teinte verte légère — identité de marque */}
            <div className="absolute inset-0 bg-primary opacity-35 mix-blend-color" />
            {/* Overlay cinéma */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/65 from-0% via-primary/26 via-[45%] to-transparent to-100%" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(135% 115% at 72% 30%, transparent 0%, rgba(6,45,32,0.30) 92%)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/55 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-primary/25 md:bg-transparent" />
          </div>
          <div className="em-container relative flex-1 flex flex-col justify-between w-full min-h-0 pt-28 md:pt-32 pb-6 md:pb-8">
            <div className="flex flex-col justify-center flex-1 min-h-0">
              <div className="max-w-xl lg:max-w-2xl">
                <p className="text-[11px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Fournisseurs partenaires</p>
                <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] text-white">
                  Nous suivons les grands acteurs de l'énergie
                </h1>
                <p className="text-base sm:text-lg text-white/85 mt-5 max-w-xl leading-relaxed">
                  Energy Market dispose d'une vision large du marché afin d'étudier, pour les particuliers comme pour les professionnels, les solutions adaptées à chaque situation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-7">
                  <SecondaryCTA className="bg-white text-primary border-white hover:bg-white/90 hover:text-primary font-bold" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full max-w-3xl mx-auto shrink-0 pt-6 md:pt-8 items-stretch">
              {[
                [`${suppliers.length}+`, "fournisseurs suivis"],
                ["100%", "approche indépendante"],
                ["France", "accompagnement national"],
              ].map(([v, label]) => (
                <div key={label} className="rounded-2xl bg-white/10 border border-white/15 px-3 py-4 sm:px-5 sm:py-5 backdrop-blur-sm flex flex-col h-full min-h-[5.75rem] sm:min-h-[6.25rem]">
                  <p className="font-display text-xl sm:text-2xl md:text-3xl font-black text-accent leading-none">{v}</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-white/80 mt-auto pt-2 leading-snug">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-auto shrink-0 w-full">
        <div className="border-y border-border bg-secondary" aria-label="Bandeau des fournisseurs">
          <p className="em-eyebrow text-center px-6 pt-4 pb-3 md:pt-5 md:pb-4">
            Les fournisseurs que nous suivons sur le marché
          </p>
          <div className="pb-4 md:pb-5 space-y-2">
            <div className="em-marq-mask overflow-hidden">
              <MarqueeRow items={suppliers} dir="l" />
            </div>
            <div className="em-marq-mask overflow-hidden">
              <MarqueeRow items={rowR} dir="r" />
            </div>
          </div>
        </div>

        <div className="shrink-0 bg-primary text-primary-foreground">
          <div className="em-container py-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {HERO_REASSURANCE.map((r) => (
              <div key={r} className="flex items-center gap-2 text-sm md:text-base font-medium">
                <Check className="h-5 w-5 text-accent shrink-0" /> {r}
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* INDÉPENDANCE */}
      <Section
        eyebrow="Notre engagement"
        title="Une approche indépendante, dans votre intérêt"
        intro="Energy Market n'est lié à aucun fournisseur. Notre rôle est de vous apporter une vision claire du marché pour vous aider à prendre une décision éclairée."
      >
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
          <div className="flex items-center gap-3 border-b border-border pb-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Scale className="h-6 w-6" aria-hidden />
            </span>
            <p className="font-display text-lg font-bold text-primary">Indépendant, par principe</p>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {[
              "Aucun lien capitalistique ou commercial exclusif avec un fournisseur",
              "Chaque dossier étudié individuellement, sans recommandation automatique",
              "Transparence sur notre méthode et nos étapes d'accompagnement",
              "Liberté de rester chez votre fournisseur actuel après l'étude",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                </span>
                <span className="leading-relaxed text-foreground">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* MÉTHODE */}
      <Section
        eyebrow="Comment ça fonctionne ?"
        title="Notre méthode de consultation du marché"
        intro="De la première prise de contact à la décision finale, chaque étape est pensée pour vous apporter clarté et sérénité — sans engagement."
        muted
      >
        <HowSteps steps={PROCESS} icons={[Search, BarChart3, Scale, HeartHandshake]} />
        <div className="mt-12">
          <p className="font-display text-lg font-bold text-primary">Selon votre profil</p>
          <p className="mt-1 text-sm text-muted-foreground">Découvrez nos accompagnements adaptés à votre situation.</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {[
              { to: "/particuliers/comparer-les-offres", label: "Comparer les offres", sub: "Particuliers", icon: Users },
              { to: "/professionnels/optimisation-des-contrats", label: "Optimiser mes contrats", sub: "Professionnels", icon: Building2 },
              { to: "/particuliers/veille-marche", label: "Veille Marché", sub: "Actualités", icon: Newspaper },
            ].map(({ to, label, sub, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                  <span className="block text-xs text-muted-foreground">{sub}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* ATOUTS */}
      <section className="bg-background">
        <div className="em-container py-16 md:py-20">
          <p className="em-eyebrow mb-3">Nos atouts</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-primary max-w-3xl">
            Ce qui guide notre accompagnement
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {ATOUTS.map((a) => (
              <Card key={a.title} className="rounded-2xl border-border bg-card">
                <CardContent className="p-7">
                  <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                    <a.icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-primary mt-5">{a.title}</h3>
                  <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{a.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <Section eyebrow="Questions fréquentes" title="Tout comprendre sur notre indépendance" muted tight>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger>{q}</AccordionTrigger>
                <AccordionContent>{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div className="mt-8">
          <RelatedLinks links={[{ to: "/faq", label: "Voir toutes les questions" }]} />
        </div>
      </Section>

      <FinalCTA title="Étudions ensemble vos solutions" />
    </div>
  );
}
